import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { appInsights } from '@sa/app/app.config';
import { UserManagementService } from '@sa/app/shared/services/user-management.service';
import { ApplicationConstants } from '@sa/app/shared/constants/ApplicationConstants';
import { MsalService } from '@azure/msal-angular';
import { environment } from '@sa/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class LogoutService {
  private msalService = inject(MsalService);
  private userManagementService = inject(UserManagementService);
  private http = inject(HttpClient);

  /**
   * Clears the concurrent-session Redis key for the currently authenticated user.
   * Safe to call on fresh login: the DELETE /session endpoint bypasses the session
   * ID check in the middleware, so it succeeds even when the stored key is stale
   * (e.g. the previous session's token had expired before DELETE /session could run).
   * Failures are swallowed so the caller's flow is never interrupted.
   */
  public async resetSessionKey(): Promise<void> {
    try {
      await this.http.delete(`${environment.baseUrl}/session`).toPromise();
    } catch {
      // Best-effort.
    }
  }

  public async logout() {
    try {
      await this.userManagementService.updateLoginStatus(false).toPromise();
    } catch {
      // Best-effort: a concurrent-session 401 or network error must not abort the
      // rest of the logout chain (DELETE /session, localStorage.clear, MSAL sign-out).
    }

    // Clear the active concurrent-session key from Redis so the user is not locked
    // out of an immediate re-login. Must run BEFORE revokeToken(): once the token is
    // denylisted the DELETE /session call would itself be rejected with 401, leaving
    // the stale session key in Redis until its TTL (MaxTokenAgeMinutes) expires.
    try {
      await this.http.delete(`${environment.baseUrl}/session`).toPromise();
    } catch {
      // Best-effort: clearing the concurrent-session key must never block logout.
    }

    // Revoke the bearer token server-side so it cannot be reused after logout.
    // Must run while MSAL still holds the account so the token can be attached.
    try {
      await this.userManagementService.revokeToken().toPromise();
    } catch {
      // Best-effort: never block logout if revocation fails. MSAL sign-out and
      // local cleanup below still proceed, and the token expires naturally.
    }

    // Preserve per-user disclaimer acknowledgements across logout so users
    // are not asked to re-accept the privacy notice on every login.
    const disclaimerEntries: { key: string; value: string }[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(ApplicationConstants.DisclaimerAcknowledgedKeyPrefix)) {
        disclaimerEntries.push({ key, value: localStorage.getItem(key) ?? 'true' });
      }
    }

    localStorage.clear();
    sessionStorage.clear();

    // Restore disclaimer acknowledgements
    for (const entry of disclaimerEntries) {
      localStorage.setItem(entry.key, entry.value);
    }

    this.clearCookies();
    await this.clearCache();
    appInsights?.clearAuthenticatedUserContext();

    this.msalService.logoutRedirect();
  }

  /**
   * Called on session/idle timeout expiry.
   * Clears local MSAL token cache without a server-side SSO logout, then
   * navigates to the login page (/) so the MSAL guard triggers a fresh login.
   * This avoids the Microsoft account-picker screen shown by logoutRedirect().
   */
  public async loginAgain() {
    try {
      await this.userManagementService.updateLoginStatus(false).toPromise();
    } catch {
      // Best-effort: must not abort DELETE /session or localStorage cleanup.
    }

    try {
      await this.http.delete(`${environment.baseUrl}/session`).toPromise();
    } catch {
      // Non-critical
    }

    const disclaimerEntries: { key: string; value: string }[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(ApplicationConstants.DisclaimerAcknowledgedKeyPrefix)) {
        disclaimerEntries.push({ key, value: localStorage.getItem(key) ?? 'true' });
      }
    }

    localStorage.clear();
    sessionStorage.clear();

    for (const entry of disclaimerEntries) {
      localStorage.setItem(entry.key, entry.value);
    }

    this.clearCookies();
    await this.clearCache();
    appInsights?.clearAuthenticatedUserContext();

    // Clear local MSAL token cache only — no server-side SSO logout.
    // Use a full page reload instead of router.navigate so all open modals,
    // overlays and Angular component state are destroyed before the login page loads.
    // replace() is used instead of href assignment so the pre-logout URL is not
    // added to the browser history and the Back button cannot return to it.
    this.msalService.instance.clearCache();
    window.location.replace('/');
  }

  private clearCookies() {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name =
        eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;Secure;SameSite=Strict`;
    }
  }
  private async clearCache() {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }
  }
}
